import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { i18n, initI18n } from "./i18n";
import { ApiError } from "./lib/api";
import { applyColorTheme } from "./lib/color-theme";
import { queryKeys } from "./lib/queries";
import { routeTree } from "./routeTree.gen";
import "./styles.css";

initI18n();

declare module "@tanstack/react-query" {
  interface Register {
    mutationMeta: {
      /** Set when the component shows errors itself (e.g. forms with field errors). */
      handlesErrors?: boolean;
    };
  }
}

/** A 401 from any API call means the session ended (expired, revoked, user disabled). */
function handleSessionEnded(error: unknown) {
  if (!(error instanceof ApiError) || error.status !== 401) return false;
  queryClient.setQueryData(queryKeys.me, null);
  applyColorTheme(null);
  const { pathname, href } = router.state.location;
  if (pathname !== "/login") {
    void router.navigate({ to: "/login", search: { redirect: href } });
  }
  return true;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) =>
        !(error instanceof ApiError && error.status >= 400 && error.status < 500) &&
        failureCount < 2,
      refetchOnWindowFocus: false,
    },
  },
  queryCache: new QueryCache({ onError: handleSessionEnded }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (handleSessionEnded(error) || mutation.meta?.handlesErrors) return;
      const code = error instanceof ApiError ? error.code : "unknown";
      toast.error(i18n.t(i18n.exists(`errors:${code}`) ? `errors:${code}` : "errors:unknown"));
    },
  }),
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const root = document.getElementById("root");
if (!root) throw new Error("Missing #root element");

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <Toaster position="top-center" richColors closeButton />
    </QueryClientProvider>
  </StrictMode>,
);
